import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  Fab,
  Fade,
  Grid,
  Skeleton,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  MenuBook as BookIcon,
  GridView as GridIcon,
  Sort as SortIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecipes } from '../context/RecipeContext';
import { ROUTES } from '../utils/constants';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { 
  BookContainer, 
  RecipeTableOfContents,
  BookPage,
  EnhancedRecipeCard,
  BookNavigation
} from '../components';

type ViewMode = 'cover' | 'toc' | 'grid';

const SKELETON_CARD_COUNT = 6;

const RecipeListScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    recipes,
    loading,
    loadingMore,
    hasMore,
    totalRecipes,
    fetchRecipes,
    loadMoreRecipes,
    setSelectedRecipe,
    setSort,
    sortBy,
    sortOrder,
    setSearch,
    searchQuery,
    favorites,
    toggleFavorite,
    onlyFavorites,
    setOnlyFavorites
  } = useRecipes();
  
  const [viewMode, setViewMode] = useState<ViewMode>('cover');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [tocPage, setTocPage] = useState<number>(0); // Track which TOC page we're on (0-indexed)
  
  // Observer for infinite scroll
  const observer = React.useRef<IntersectionObserver | null>(null);
  const lastRecipeElementRef = React.useCallback((node: HTMLDivElement | null) => {
    if (loading || loadingMore) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreRecipes();
      }
    });
    
    if (node) {
      observer.current.observe(node);
    }
  }, [loading, loadingMore, hasMore, loadMoreRecipes]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleRecipeClick = (recipeId: string) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (recipe) {
      setSelectedRecipe(recipe);
      navigate(`/recipes/${recipeId}`);
    }
  };

  const handleAddRecipe = () => {
    navigate(ROUTES.RECIPE_CAPTURE);
  };

  const handleClearSearch = () => {
    setSearch('');
  };

  const handleOpenBook = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode('toc');
      setIsTransitioning(false);
    }, 300);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode(mode);
      setIsTransitioning(false);
    }, 300);
  };

  const handleTocNextPage = () => {
    console.log('handleTocNextPage called', { tocPage, recipesLength: recipes.length, hasMore });
    const recipesPerTocSpread = 12; // 12 recipes per TOC spread (6 on each page)
    const totalTocPages = Math.ceil(recipes.length / recipesPerTocSpread);
    console.log('totalTocPages:', totalTocPages);

    // Move to next page if we have recipes loaded for it
    if (tocPage < totalTocPages - 1) {
      console.log('Moving to next page:', tocPage + 1);
      setTocPage(tocPage + 1);
    } else if (hasMore) {
      console.log('Loading more recipes...');
      // If we're on the last page but there are more recipes to load, load them first
      loadMoreRecipes().then(() => {
        // After loading, advance to the next page
        console.log('Loaded more, advancing to page:', tocPage + 1);
        setTocPage(tocPage + 1);
      });
      return;
    }

    // Preload more recipes if we're getting close to the end
    const nextPageEndIndex = (tocPage + 2) * recipesPerTocSpread;
    if (nextPageEndIndex >= recipes.length && hasMore) {
      console.log('Preloading more recipes...');
      loadMoreRecipes();
    }
  };

  const handleTocPreviousPage = () => {
    if (tocPage > 0) {
      setTocPage(tocPage - 1);
    }
  };

  const canGoToNextTocPage = () => {
    const recipesPerTocSpread = 12;
    const totalTocPages = Math.ceil(recipes.length / recipesPerTocSpread);
    // Can go to next page if there are more loaded recipes OR more to load from server
    return tocPage < totalTocPages - 1 || hasMore;
  };

  const canGoToPreviousTocPage = () => {
    return tocPage > 0;
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (newSortBy: 'title' | 'ingredients_count', newSortOrder: 'asc' | 'desc') => {
    setSort(newSortBy, newSortOrder);
    handleSortClose();
  };

  const handleToggleFavoritesFilter = () => {
    setOnlyFavorites(!onlyFavorites);
  };

  return (
    <BookContainer>
      <Box sx={{ 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #F5E6D3 0%, #E8D5B7 100%)',
      }}>
        {/* View Mode Toggle */}
        {viewMode !== 'cover' && (
          <Fade in={!isTransitioning}>
            <Box sx={{ 
              position: 'absolute', 
              top: 20, 
              right: 20, 
              zIndex: 10,
              display: 'flex',
              gap: 1,
            }}>
              <IconButton
                onClick={() => handleViewModeChange('toc')}
                sx={{
                  backgroundColor: viewMode === 'toc' ? 'primary.main' : 'background.paper',
                  color: viewMode === 'toc' ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: viewMode === 'toc' ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <BookIcon />
              </IconButton>
              <IconButton
                onClick={() => handleViewModeChange('grid')}
                sx={{
                  backgroundColor: viewMode === 'grid' ? 'primary.main' : 'background.paper',
                  color: viewMode === 'grid' ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: viewMode === 'grid' ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <GridIcon />
              </IconButton>
            </Box>
          </Fade>
        )}

        {/* Search Bar and Sort (for grid view) */}
        {viewMode === 'grid' && (
          <Fade in={!isTransitioning}>
            <Box sx={{ p: 2, backgroundColor: 'background.paper', boxShadow: 1 }}>
              <Container maxWidth="md" sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
              }}>
                <TextField
                  fullWidth
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClearSearch} edge="end" size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 3,
                      fontFamily: '"Crimson Text", serif',
                    },
                  }}
                />
                <Button
                  variant={onlyFavorites ? "contained" : "outlined"}
                  color={onlyFavorites ? "secondary" : "primary"}
                  startIcon={onlyFavorites ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  onClick={handleToggleFavoritesFilter}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Favorites
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SortIcon />}
                  onClick={handleSortClick}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Sort
                </Button>
                <Menu
                  anchorEl={sortAnchorEl}
                  open={Boolean(sortAnchorEl)}
                  onClose={handleSortClose}
                >
                  <MenuItem 
                    onClick={() => handleSortSelect('title', 'asc')}
                    selected={sortBy === 'title' && sortOrder === 'asc'}
                  >
                    Name (A-Z)
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('title', 'desc')}
                    selected={sortBy === 'title' && sortOrder === 'desc'}
                  >
                    Name (Z-A)
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('ingredients_count', 'asc')}
                    selected={sortBy === 'ingredients_count' && sortOrder === 'asc'}
                  >
                    Complexity (Low-High)
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('ingredients_count', 'desc')}
                    selected={sortBy === 'ingredients_count' && sortOrder === 'desc'}
                  >
                    Complexity (High-Low)
                  </MenuItem>
                </Menu>
              </Container>
            </Box>
          </Fade>
        )}

        {/* Main Content */}
        <Box sx={{ position: 'relative' }}>
          <AnimatePresence mode="wait">
            {!isTransitioning && (
              <motion.div
                key={viewMode}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                transition={{ duration: 0.3 }}
                style={{ width: '100%' } as React.CSSProperties}
              >
                {/* Title Page Spread */}
                {viewMode === 'cover' && (
                  <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    py: 4,
                  }}>
                    <Box
                      onClick={handleOpenBook}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        gap: 0,
                        maxWidth: { xs: '100%', md: 'lg' },
                        width: '100%',
                        cursor: 'pointer',
                        '&:hover': {
                          '& .left-page .subtitle': {
                            color: '#FF6B35',
                          },
                          '& .right-page .title': {
                            transform: 'scale(1.02)',
                          }
                        }
                      }}
                    >
                      {/* Left page (Intro) */}
                      <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                        <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.RECIPE_LIST_COVER_LEFT}>
                        <Box
                          className="left-page"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            px: { xs: 2, sm: 3, md: 4 },
                            py: { xs: 4, md: 6 },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 600,
                              color: '#8B4513',
                              mb: 3,
                              textAlign: 'center',
                            }}
                          >
                            Welcome to
                          </Typography>

                          <Typography
                            className="subtitle"
                            variant="h4"
                            sx={{
                              fontFamily: '"Crimson Text", serif',
                              fontStyle: 'italic',
                              color: '#8B4513',
                              mb: 4,
                              textAlign: 'center',
                              transition: 'color 0.3s ease',
                            }}
                          >
                            A Personal Culinary Journey
                          </Typography>

                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: '"Crimson Text", serif',
                              color: '#8B4513',
                              opacity: 0.8,
                              lineHeight: 1.8,
                              textAlign: 'justify',
                              mb: 4,
                            }}
                          >
                            Within these pages, you'll discover a carefully curated collection of cherished recipes, 
                            each one telling its own story of flavors, memories, and culinary adventures.
                          </Typography>

                          <Box
                            sx={{
                              textAlign: 'center',
                              mt: 'auto',
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: '"Crimson Text", serif',
                                color: '#8B4513',
                                opacity: 0.6,
                                fontStyle: 'italic',
                              }}
                            >
                              Click anywhere to begin exploring
                            </Typography>
                          </Box>
                        </Box>
                      </BookPage>
                      </Box>

                      {/* Right page (Title) */}
                      <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                        <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.RECIPE_LIST_COVER_RIGHT}>
                        <Box
                          className="right-page"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            px: { xs: 2, sm: 3, md: 4 },
                            py: { xs: 4, md: 6 },
                          }}
                        >
                          {/* Decorative top border */}
                          <Box
                            sx={{
                              width: '200px',
                              height: '2px',
                              background: 'linear-gradient(90deg, transparent 0%, #8B4513 50%, transparent 100%)',
                              mb: 4,
                            }}
                          />
                          
                          {/* Main title */}
                          <Typography
                            className="title"
                            variant="h1"
                            sx={{
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 700,
                              color: '#8B4513',
                              mb: 2,
                              transition: 'transform 0.3s ease',
                              lineHeight: 1.1,
                              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                            }}
                          >
                            My Recipe
                          </Typography>
                          <Typography
                            className="title"
                            variant="h1"
                            sx={{
                              fontFamily: '"Playfair Display", serif',
                              fontWeight: 700,
                              color: '#8B4513',
                              mb: 4,
                              transition: 'transform 0.3s ease',
                              lineHeight: 1.1,
                              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                            }}
                          >
                            Collection
                          </Typography>

                          {/* Decorative flourish */}
                          <Box
                            sx={{
                              fontSize: '2.5rem',
                              color: '#D2B48C',
                              mb: 4,
                              fontFamily: 'serif',
                            }}
                          >
                            ❦
                          </Box>

                          {/* Recipe count */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: '"Crimson Text", serif',
                              color: '#8B4513',
                              mb: 4,
                              opacity: 0.7,
                            }}
                          >
                            {totalRecipes} Treasured Recipe{totalRecipes !== 1 ? 's' : ''}
                          </Typography>

                          {/* Decorative bottom border */}
                          <Box
                            sx={{
                              width: '150px',
                              height: '2px',
                              background: 'linear-gradient(90deg, transparent 0%, #8B4513 50%, transparent 100%)',
                            }}
                          />
                        </Box>
                      </BookPage>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Table of Contents View */}
                {viewMode === 'toc' && (() => {
                  const recipesPerTocSpread = 12;
                  const startIndex = tocPage * recipesPerTocSpread;
                  const endIndex = startIndex + recipesPerTocSpread;
                  const currentPageRecipes = recipes.slice(startIndex, endIndex);
                  const leftPageRecipes = currentPageRecipes.slice(0, 6);
                  const rightPageRecipes = currentPageRecipes.slice(6);

                  return (
                    <Box sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 2,
                      py: 4,
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        gap: 0,
                        maxWidth: { xs: '100%', md: 'lg' },
                        width: '100%',
                      }}>
                        {/* Left page - Table of Contents */}
                        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                          <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.RECIPE_TOC_LEFT + (tocPage * 2)}>
                            <RecipeTableOfContents
                              recipes={leftPageRecipes}
                              onRecipeSelect={handleRecipeClick}
                              startIndex={startIndex}
                            />
                          </BookPage>
                        </Box>

                        {/* Right page - Continued Table of Contents */}
                        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                          <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.RECIPE_TOC_RIGHT + (tocPage * 2)}>
                            <RecipeTableOfContents
                              recipes={rightPageRecipes}
                              onRecipeSelect={handleRecipeClick}
                              startIndex={startIndex + 6}
                            />
                          </BookPage>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      {loading && recipes.length === 0
                        ? Array.from({ length: SKELETON_CARD_COUNT }).map((_, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={idx}>
                              <Skeleton
                                variant="rectangular"
                                animation="wave"
                                sx={{
                                  width: '100%',
                                  height: 320,
                                  borderRadius: 3,
                                  bgcolor: 'background.paper',
                                }}
                              />
                            </Grid>
                          ))
                        : recipes.map((recipe, index) => {
                            // Attach ref to the last element for infinite scroll
                            const isLastElement = index === recipes.length - 1;
                            return (
                              <Grid 
                                item 
                                xs={12} 
                                sm={6} 
                                md={4} 
                                key={recipe.id}
                                ref={isLastElement ? lastRecipeElementRef : null}
                              >
                                <motion.div
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index % 12 * 0.05 }} // Stagger only within current batch
                                >
                                  <EnhancedRecipeCard
                                    recipe={recipe}
                                    onClick={handleRecipeClick}
                                    isFavorite={favorites.includes(recipe.id)}
                                    onFavoriteToggle={toggleFavorite}
                                  />
                                </motion.div>
                              </Grid>
                            );
                          })}
                          
                      {/* Loading indicator for pagination */}
                      {loadingMore && (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress size={32} color="primary" />
                        </Box>
                      )}
                    </Grid>
                    
                    {!loading && recipes.length === 0 && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 8,
                      }}>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontFamily: '"Playfair Display", serif',
                            color: 'text.secondary',
                            mb: 2,
                          }}
                        >
                          No recipes found
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontFamily: '"Crimson Text", serif',
                            color: 'text.secondary',
                          }}
                        >
                          Try adjusting your search or add a new recipe
                        </Typography>
                      </Box>
                    )}
                  </Container>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Floating Action Button - positioned relative to book */}
        {viewMode !== 'cover' && (
          <Fab
            color="primary"
            aria-label="add recipe"
            onClick={handleAddRecipe}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, md: 80 }, // Above the book navigation, adjusted for mobile
              right: { xs: 16, md: 24 },
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease-in-out',
              zIndex: 999,
            }}
          >
            <AddIcon />
          </Fab>
        )}
        
        {/* Book Navigation */}
        <BookNavigation
          viewMode={viewMode}
          onTocNext={handleTocNextPage}
          onTocPrevious={handleTocPreviousPage}
          canTocNext={canGoToNextTocPage()}
          canTocPrevious={canGoToPreviousTocPage()}
        />
      </Box>
    </BookContainer>
  );
};

export default RecipeListScreen;
